import PartySocket from 'partysocket';
import { PARTYKIT_HOST, NETWORK } from './constants.js';
import { updateConnectionStatus } from './ui.js';
import type { PlaneState } from './plane.js';
import {
  parseServerMessage,
  type ServerMessage,
} from './multiplayer-protocol.js';

// WebSocket readyState constants (not available in ES modules)
const WS_OPEN = 1;

let socket: PartySocket | null = null;
let lastSendTime = 0;
let isConnected = false;
let hasEverConnected = false;

/**
 * Welcome message from server
 */
export type WelcomeMessage = Extract<ServerMessage, { type: 'welcome' }>;

/**
 * Player joined message
 */
export interface PlayerJoinedMessage {
  type: 'playerJoined';
  player: PlaneState;
}

/**
 * Player left message
 */
export interface PlayerLeftMessage {
  type: 'playerLeft';
  id: string;
}

/**
 * Sync message from server
 */
export interface SyncMessage {
  type: 'sync';
  planes: Record<string, PlaneState>;
}

/**
 * Network callbacks interface
 */
export interface NetworkCallbacks {
  onWelcome: (msg: WelcomeMessage) => void;
  onSync: (planes: Record<string, PlaneState>) => void;
  onPlayerUpdated: (player: PlaneState) => void;
  onPlayerLeft: (id: string) => void;
  onPlayerJoined: (player: PlaneState) => void;
  onDisconnect?: () => void;
  onReconnect?: () => void;
}

/**
 * Connection interface
 */
export interface Connection {
  sendPosition: (planeState: PlaneState) => void;
  sendTeleport: (lat: number, lng: number) => void;
  isConnected: () => boolean;
  close: () => void;
}

/**
 * Connect to the PartyKit server
 */
export function createConnection(roomId: string, callbacks: NetworkCallbacks): Connection {
  if (!PARTYKIT_HOST) {
    console.error('PartyKit host not configured');
    return createDisconnectedInterface();
  }

  socket = new PartySocket({
    host: PARTYKIT_HOST,
    room: roomId,
  });

  socket.addEventListener('open', () => {
    const wasConnected = isConnected;
    isConnected = true;
    updateConnectionStatus('connected');

    if (wasConnected === false && hasEverConnected && callbacks.onReconnect) {
      callbacks.onReconnect();
    }
    hasEverConnected = true;
  });

  socket.addEventListener('message', (event) => {
    try {
      const msg = parseServerMessage(JSON.parse(event.data as string));
      if (!msg) {
        console.warn('Ignoring invalid server message');
        return;
      }

      switch (msg.type) {
        case 'welcome':
          callbacks.onWelcome(msg);
          break;
        case 'sync':
          callbacks.onSync(msg.planes);
          break;
        case 'playerJoined':
          callbacks.onPlayerJoined(msg.player);
          break;
        case 'playerUpdated':
          callbacks.onPlayerUpdated(msg.player);
          break;
        case 'playerLeft':
          callbacks.onPlayerLeft(msg.id);
          break;
        case 'error':
          console.warn('Multiplayer server:', msg.message);
          break;
      }
    } catch (e) {
      console.error('Failed to parse message:', e);
    }
  });

  socket.addEventListener('close', () => {
    const wasConnected = isConnected;
    isConnected = false;
    updateConnectionStatus('connecting'); // Will auto-reconnect

    if (wasConnected && callbacks.onDisconnect) {
      callbacks.onDisconnect();
    }
    // PartySocket auto-reconnects by default
  });

  socket.addEventListener('error', (e) => {
    console.error('WebSocket error:', e);
  });

  return {
    /**
     * Send position update to server (throttled)
     */
    sendPosition: (planeState: PlaneState): void => {
      const now = Date.now();
      if (now - lastSendTime < NETWORK.UPDATE_RATE) return;
      lastSendTime = now;

      if (socket && socket.readyState === WS_OPEN) {
        socket.send(JSON.stringify({
          type: 'position',
          data: {
            lat: planeState.lat,
            lng: planeState.lng,
            altitude: planeState.altitude,
            heading: planeState.heading,
            pitch: planeState.pitch,
            roll: planeState.roll,
            speed: planeState.speed,
          },
        }));
      }
    },

    /**
     * Send teleport notification
     */
    sendTeleport: (lat: number, lng: number): void => {
      if (socket && socket.readyState === WS_OPEN) {
        socket.send(JSON.stringify({
          type: 'teleport',
          data: { lat, lng },
        }));
      }
    },

    /**
     * Check if connected
     */
    isConnected: (): boolean => isConnected,

    /**
     * Close the connection
     */
    close: (): void => {
      const wasConnected = isConnected;
      isConnected = false;
      const activeSocket = socket;
      socket = null;
      activeSocket?.close();
      if (wasConnected && callbacks.onDisconnect) {
        callbacks.onDisconnect();
      }
    },
  };
}

/**
 * Create a disconnected interface for when server is not configured
 */
function createDisconnectedInterface(): Connection {
  // Update UI to show offline status
  updateConnectionStatus('disconnected');

  return {
    sendPosition: (): void => {},
    sendTeleport: (): void => {},
    isConnected: (): boolean => false,
    close: (): void => {},
  };
}
